import {useCallback, useEffect, useRef, useState} from "react";
import {STORY_DURATION} from "../stories.constants";

/**
 * @typedef {Object} StoryViewerState
 * @property {number} currentIndex - Active story index
 * @property {number} progress - Progress 0–100 for current story
 * @property {boolean} isPaused - Whether the timer is paused
 * @property {boolean} isOpen - Whether the viewer is open
 */

/**
 * Hook that drives the story viewer state machine.
 *
 * Responsibilities:
 * - open / close viewer
 * - track current index & progress
 * - auto-advance after STORY_DURATION (images) or video duration
 * - pause on hover / long-press, resume on release
 * - next / prev navigation
 * - keyboard navigation (ArrowRight, ArrowLeft, Escape)
 *
 * @param {Object} opts
 * @param {Array<{id: string, type?: string, duration?: number}>} opts.stories
 * @param {number} [opts.initialIndex=0]
 * @param {() => void} opts.onClose
 * @param {(id: string) => void} [opts.onViewed]
 */
const useStoryViewer = ({stories, initialIndex = 0, onClose, onViewed, onComplete}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const timerRef = useRef(null);
  const startTimeRef = useRef(Date.now());
  const elapsedBeforePauseRef = useRef(0);

  const duration = stories[currentIndex]?.duration || STORY_DURATION;

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    clearTimer();
    startTimeRef.current = Date.now();

    timerRef.current = setInterval(() => {
      const elapsed = elapsedBeforePauseRef.current + (Date.now() - startTimeRef.current);
      const pct = Math.min((elapsed / duration) * 100, 100);
      setProgress(pct);

      if (pct >= 100) {
        clearTimer();
        onViewed?.(stories[currentIndex]?.id);

        if (currentIndex < stories.length - 1) {
          setCurrentIndex(prev => prev + 1);
        } else if (onComplete) {
          onComplete();
        } else {
          onClose();
        }
      }
    }, 30);
  }, [currentIndex, stories, duration, onClose, onComplete, onViewed, clearTimer]);

  // Reset progress & start timer when index changes
  useEffect(() => {
    setProgress(0);
    elapsedBeforePauseRef.current = 0;
    setIsPaused(false);
    startTimer();
    return clearTimer;
  }, [currentIndex, startTimer, clearTimer]);

  const goNext = useCallback(() => {
    clearTimer();
    onViewed?.(stories[currentIndex]?.id);
    if (currentIndex < stories.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else if (onComplete) {
      onComplete();
    } else {
      onClose();
    }
  }, [currentIndex, stories, onClose, onComplete, onViewed, clearTimer]);

  const goPrev = useCallback(() => {
    clearTimer();
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    } else {
      // Restart current story
      elapsedBeforePauseRef.current = 0;
      setProgress(0);
      startTimer();
    }
  }, [currentIndex, clearTimer, startTimer]);

  const pause = useCallback(() => {
    if (isPaused) return;
    setIsPaused(true);
    elapsedBeforePauseRef.current += Date.now() - startTimeRef.current;
    clearTimer();
  }, [isPaused, clearTimer]);

  const resume = useCallback(() => {
    if (!isPaused) return;
    setIsPaused(false);
    startTimer();
  }, [isPaused, startTimer]);

  // Keyboard navigation
  useEffect(() => {
    const onKey = e => {
      switch (e.key) {
        case "ArrowRight":
          goNext();
          break;
        case "ArrowLeft":
          goPrev();
          break;
        case "Escape":
          onClose();
          break;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goNext, goPrev, onClose]);

  return {
    currentIndex,
    progress,
    isPaused,
    story: stories[currentIndex],
    totalStories: stories.length,
    goNext,
    goPrev,
    pause,
    resume,
  };
};

export default useStoryViewer;
